import { useWatch } from "react-hook-form";
import { Input } from "antd";
import { Controller } from "react-hook-form";
import styled from "styled-components";

const { TextArea } = Input;

const SubTitle = styled.div`
    display: flex;
    align-items: start;
    gap: 4px;
    margin-bottom: 8px;

    .play {
        color: rgba(255, 255, 255, 0.45);
        transition: all 0.3s ease;
        cursor: pointer;

        &:hover {
            color: rgba(255, 255, 255, 0.8);
        }
    }

    .time {
        display: flex;
        flex: 0 0 110px;
        align-items: center;
        border-bottom: 1px solid rgba(255, 255, 255, 0.45);

        > span:last-child {
            display: flex;
            align-items: center;

            input {
                color: rgba(255, 255, 255, 0.45);
            }
        }

        > span:nth-child(-n+2){
            display: flex;
            align-items: center;

            &:after {
                content: ":";
                font-size: 12px;
                color: rgb(0, 0, 0);
                margin: 0 2px;
            }
        }

        input {
            font-size: 12px;
            text-align: center;
        }
    }

    input,
    textarea {
        border-color: transparent;
        border-radius: 0;
        padding: 0;
        background-color: transparent !important;
        color: rgba(255, 255, 255, 0.8);

        &::placeholder {
            color: rgba(255, 255, 255, 0.15);
        }
    }

    textarea {
        background: rgba(255, 255, 255, 0.05) !important;
        padding: 4px 8px;
        border-radius: 2px;
    }

    ${(props) => props.isActive && `
        background-color: rgba(255, 255, 0, 0.1); // Light yellow highlight
    `}
`;

const numberValidate = (config) => {
    const { maxNumber, unit } = config;
    return (data = "") => {
        const value = parseInt(data);
        if (isNaN(value)) {
            return "Please enter a number.";
        }
        if (value < 0) {
            return "The number must not be less than 0.";
        }
        if (unit !== "hours" && maxNumber !== undefined && value > maxNumber) {
            return `The number must not exceed ${maxNumber}.`;
        }
        return true;
    };
};

const textValidate = () => {
    return (data = "") => {
        if (data.trim().length === 0) {
            return "Please enter subtitles.";
        }
        return true;
    };
};

const TimeInput = ({
                       index,
                       fieldName,
                       control,
                       id,
                       onSelectAll,
                       maxNumber,
                       unit,
                       validate
                   }) => {
    return (
        <Controller
            name={`list.${index}.${fieldName}`}
            control={control}
            rules={{ validate }}
            render={({ field, fieldState }) => (
                <Input
                    className="start-hours"
                    placeholder={unit}
                    key={id}
                    defaultValue={field.value}
                    onClick={onSelectAll}
                    {...field}
                    onBlur={undefined}
                    status={fieldState.error ? "error" : undefined}
                    disabled={false}
                    // style={{ cursor: "edit", border: "none" }}
                />
            )}
        />
    );
};

export default (props) => {
    const { data, index, globalIndex, control, onSeekAndPlay, onReplay, onPause, currentTime, isPlaying, duration } = props;

    // Watch form fields for real-time updates - use globalIndex for field names
    const startHours = useWatch({ control, name: `list.${globalIndex}.startHours` });
    const startMinutes = useWatch({ control, name: `list.${globalIndex}.startMinutes` });
    const startSeconds = useWatch({ control, name: `list.${globalIndex}.startSeconds` });
    const startMilliseconds = useWatch({ control, name: `list.${globalIndex}.startMilliseconds` });

    const endHours = useWatch({ control, name: `list.${globalIndex}.endHours` });
    const endMinutes = useWatch({ control, name: `list.${globalIndex}.endMinutes` });
    const endSeconds = useWatch({ control, name: `list.${globalIndex}.endSeconds` });
    const endMilliseconds = useWatch({ control, name: `list.${globalIndex}.endMilliseconds` });

    // Calculate time in seconds
    const calculateTime = (hours, minutes, seconds, milliseconds) => {
        return (
            parseInt(hours || 0) * 3600 +
            parseInt(minutes || 0) * 60 +
            parseInt(seconds || 0) +
            parseInt(milliseconds || 0) / 1000
        );
    };

    const startTime = calculateTime(startHours, startMinutes, startSeconds, startMilliseconds);
    const endTime = calculateTime(endHours, endMinutes, endSeconds, endMilliseconds);

    // Determine if this row is active
    const isActive = currentTime >= startTime && currentTime < endTime;

    const validateTimeField = (timeType, unit, maxNumber) => (value, formValues) => {
        const numberValidation = numberValidate({ maxNumber, unit })(value);
        if (numberValidation !== true) {
            return numberValidation;
        }

        // Calculate total time for start or end
        const row = formValues.list[globalIndex];
        const hours = row[`${timeType}Hours`];
        const minutes = row[`${timeType}Minutes`];
        const seconds = row[`${timeType}Seconds`];
        const milliseconds = row[`${timeType}Milliseconds`];
        const totalTime = calculateTime(hours, minutes, seconds, milliseconds);

        // Check against video duration
        if (duration !== null && totalTime > duration) {
            return `${timeType.charAt(0).toUpperCase() + timeType.slice(1)} time exceeds video duration`;
        }

        return true;
    };

    const handleButtonClick = () => {
        if (isActive && isPlaying) {
            onPause();
        } else {
            onSeekAndPlay(startTime);
        }
    };

    const handleReplayClick = () => {
        onReplay(startTime, endTime); // Call the replay handler with row-specific times
    };

    const config = {
        id: data.id,
        control,
        index: globalIndex, // Use globalIndex for accessing form fields
        onSelectAll: (e) => {
            e.target.select();
        }
    };

    return (
        <SubTitle key={props.id} isActive={isActive}>
            <div className="play" onClick={handleButtonClick}>
                {isActive && isPlaying ? (
                    <i className="fa-regular fa-circle-pause"></i>
                ) : (
                    <i className="fa-regular fa-circle-play"></i>
                )}
            </div>
            <div className="play" onClick={handleReplayClick} title="Replay this segment">
                <i className="fa-solid fa-rotate-right"></i>
            </div>
            <Controller
                name={`list.${globalIndex}.text`} // Use globalIndex to access the correct field
                control={props.control}
                rules={{ validate: textValidate() }}
                render={({ field, fieldState }) => {
                    const config = {
                        defaultValue: field.value,
                        placeholder: "Please enter subtitles",
                        id: `subtitle-text_${props.index}`, // Keep using index for DOM elements
                        status: fieldState.error ? "error" : undefined,
                        ...field,
                        onKeyDown: (e) => {
                            const textarea = e.target;
                            const text = textarea.value;
                            const selectionStart = textarea.selectionStart;
                            const selectionEnd = textarea.selectionEnd;
                            const contentLength = text.length;

                            if (e.key === "Enter" && e.shiftKey) {
                                e.preventDefault(); // Prevent default newline insertion
                                const textBeforeCursor = text.slice(0, selectionStart);
                                const textAfterCursor = text.slice(selectionStart); // Assumes split at cursor start
                                if (textBeforeCursor.length > 0 && textAfterCursor.length > 0) {
                                    props.onNextRow(props.index, [
                                        textBeforeCursor,
                                        textAfterCursor
                                    ]);
                                } else if (textAfterCursor.length === 0) {
                                    props.onAddRowAfter?.(props.index, "");
                                } else if (textBeforeCursor.length === 0) {
                                    props.onAddRowBefore?.(props.index, "");
                                }
                                return;
                            }

                            const isTextSelected = selectionStart !== selectionEnd;

                            if (isTextSelected) {
                                // If text is selected, Backspace and Delete should only perform their default action (delete selection).
                                if (e.key === "Backspace" || e.key === "Delete") {
                                    return; // Allow default action to delete selected text
                                }
                            } else {
                                // No text selected, cursor is a point.
                                // Row combining logic via Backspace or Delete is removed as per user request.
                                // Default Backspace/Delete behavior will apply if not at start/end of non-empty field,
                                // or do nothing if at start/end of empty field.
                                if (e.key === "Delete") {
                                    // Allow default delete behavior within the text area
                                }

                                if (e.key === "Backspace") {
                                    // Allow default backspace behavior within the text area
                                }
                            }
                        }
                    };
                    return <TextArea {...config} autoSize />;
                }}
            />
            <span className="time-container">
                <div className="time">
                    <span>
                        <TimeInput
                            {...config}
                            fieldName="startHours"
                            id={`startHours_${index}`} // Use local index for DOM element IDs
                            validate={validateTimeField('start', 'hours')}
                        />
                    </span>
                    <span>
                        <TimeInput
                            {...config}
                            fieldName="startMinutes"
                            id={`startMinutes_${index}`}
                            validate={validateTimeField('start', 'minutes', 59)}
                        />
                    </span>
                    <span>
                        <TimeInput
                            {...config}
                            fieldName="startSeconds"
                            id={`startSeconds_${index}`}
                            validate={validateTimeField('start', 'seconds', 59)}
                        />
                    </span>
                    <span style={{ display: "none" }}>
                        <TimeInput
                            {...config}
                            fieldName="startMilliseconds"
                            id={`startMilliseconds_${index}`}
                            validate={validateTimeField('start', 'milliseconds', 999)}
                        />
                    </span>
                </div>
                <div className="separator">|</div>
                <div className="time">
                    <span>
                        <TimeInput
                            {...config}
                            fieldName="endHours"
                            id={`endHours_${index}`}
                            validate={validateTimeField('end', 'hours')}
                        />
                    </span>
                    <span>
                        <TimeInput
                            {...config}
                            fieldName="endMinutes"
                            id={`endMinutes_${index}`}
                            validate={validateTimeField('end', 'minutes', 59)}
                        />
                    </span>
                    <span>
                        <TimeInput
                            {...config}
                            fieldName="endSeconds"
                            id={`endSeconds_${index}`}
                            validate={validateTimeField('end', 'seconds', 59)}
                        />
                    </span>
                    <span style={{ display: "none" }}>
                        <TimeInput
                            {...config}
                            fieldName="endMilliseconds"
                            id={`endMilliseconds_${index}`}
                            validate={validateTimeField('end', 'milliseconds', 999)}
                        />
                    </span>
                </div>
            </span>
            <div className="row-actions">
                <button
                    className="icon-button"
                    onClick={() => props.onInsertAfter(props.index)}
                    title="Insert after with same time"
                >
                    <i className="fa-solid fa-plus"></i>
                </button>
                <button
                    className="icon-button"
                    onClick={() => props.onRemove(props.index)}
                    title="Remove this row"
                >
                    <i className="fa-solid fa-trash"></i>
                </button>
            </div>
        </SubTitle>
    );
};
